import { canSelectRevisionTemplate } from 'utils/revision-workflow';
import { useApi } from 'api/api-context';
import DashboardLayout from 'layouts/dashboard/DashboardLayout';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  DASHBOARD_PAGE_PATH,
  FORM_PAGE_PATH,
  FORMS_PAGE_PATH,
  REVISIONS_PAGE_PATH,
  REVISION_PAGE_PATH,
  SELECT_FORM_TEMPLATE_AND_VISUAL_PAGE_PATH,
  SELECT_REVISION_TEMPLATE_AND_VISUAL_PAGE_PATH,
} from 'routes/paths';
import panic from 'errors/panic';
import { NO_ENTITY_EXISTS_ERROR_CODE } from 'utils/constants';
import { RevisionGetResponse } from 'api/actions/revision-get/revision-get-response';
import FillOutRevisionForm from 'components/forms/revision/fill-out/FillOutRevisionForm';
import { Box } from '@mantine/core';

/**
 * Page used to fill out a revision.
 */
export default function FillOutRevisionPage() {
  const navigate = useNavigate();
  const { revisionId } = useParams();
  const { getAction, userId } = useApi();
  const [revision, setRevision] = useState<RevisionGetResponse | null>(null);

  const refetchRevision = useCallback(async () => {
    if (!revisionId) {
      return null;
    }

    try {
      const action = getAction('RevisionGet');
      const nextRevision = await action({ parameters: { revisionId } });

      setRevision(nextRevision);

      return nextRevision;
    } catch (e: any) {
      if (e?.response?.data?.error?.code === NO_ENTITY_EXISTS_ERROR_CODE) {
        navigate(DASHBOARD_PAGE_PATH.original, { replace: true });
      } else {
        panic(e);
      }

      throw e;
    }
  }, [getAction, navigate, revisionId]);

  const revisionDetailPath = useMemo(
    () =>
      revision?.kind === 'revision' ? REVISION_PAGE_PATH.insert({ revisionId }) : FORM_PAGE_PATH.insert({ revisionId }),
    [revision?.kind, revisionId]
  );

  const isDelegatedToMe = useMemo(
    () => revision?.delegatedTo?.some((user) => user.userId === userId),
    [revision, userId]
  );

  const breadcrumbs = useMemo(
    () => [
      {
        title: revision?.kind === 'revision' ? 'Revízne správy' : 'Formuláre',
        link: revision?.kind === 'revision' ? REVISIONS_PAGE_PATH.original : FORMS_PAGE_PATH.original,
      },
      { title: revision ? `${revision.revisionName} (${revision?.revisionTemplate?.name})` : 'Načítavam' },
    ],
    [revision]
  );

  // Fetch revision
  useEffect(() => {
    if (revisionId) {
      refetchRevision().catch(() => undefined);
    }
  }, [refetchRevision, revisionId]);

  if (
    revision &&
    (!revision.technician ||
      (revision.technician?.userId !== userId && !isDelegatedToMe) ||
      (revision.revisionStatus.slug !== 'in-progress' && !canSelectRevisionTemplate(revision)))
  ) {
    return <Navigate to={revisionDetailPath} replace />;
  }

  if (revision && canSelectRevisionTemplate(revision)) {
    return (
      <Navigate
        to={
          revision.kind === 'revision'
            ? SELECT_REVISION_TEMPLATE_AND_VISUAL_PAGE_PATH.insert({ revisionId })
            : SELECT_FORM_TEMPLATE_AND_VISUAL_PAGE_PATH.insert({ revisionId })
        }
        replace
      />
    );
  }

  return (
    <DashboardLayout breadcrumbs={breadcrumbs}>
      <Box pt={20}>{revision && <FillOutRevisionForm revision={revision} refetchRevision={refetchRevision} />}</Box>
    </DashboardLayout>
  );
}
